import { useTranslation } from 'react-i18next';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';
import { RegexExpressions } from 'shared/constants/regex/regex-expressions';

import { useGatewaysContextProvider } from 'features/gateway/context/gateway-provider';
import {
  validateGatewayIpAddress,
  validateGatewayName,
  validateSingleMediaServerGateway,
} from 'features/gateway/domain/queries/gateway-queries';
import { GatewayType } from '../domain/models/gateway-type';

interface GatewayValidationHook {
  validateName: () => Promise<boolean | string>;
  validateIpAddress: (ipAddress: string) => boolean | string;
  validateVirtualIpAddress: () => Promise<boolean | string>;
  validatePassword: (password: string) => boolean | string;
  validateSingularityOfMediaServerGateway: (gatewayType: GatewayType) => Promise<boolean | string>;
  validateChildGatewayName: (index: number) => Promise<boolean | string>;
}

const useGatewayValidation = (): GatewayValidationHook => {
  const { t } = useTranslation('gateway');
  const { authenticatedInstance } = useAxiosClientsContextProvider();

  const { gatewayDetailsHook } = useGatewaysContextProvider();

  const validateName = async (): Promise<boolean | string> => {
    const name = gatewayDetailsHook.form.getValues('name')?.trim();
    if (!name) return true;

    const id = gatewayDetailsHook.currentSelectedGateway?.id;

    try {
      await validateGatewayName(authenticatedInstance, {
        id,
        name,
      });
      return true;
    } catch {
      return t('details.information.uniqueNameHelperText');
    }
  };

  const validateVirtualIpAddress = async (): Promise<boolean | string> => {
    const ipAddress = gatewayDetailsHook.form.getValues('ipAddress')?.trim();
    if (!ipAddress) return true;

    const regex = new RegExp(RegexExpressions.IP_ADDRESS);
    if (!regex.test(ipAddress)) {
      return t('details.information.invalidVirtualIpAddressHelperText');
    }

    const id = gatewayDetailsHook.currentSelectedGateway?.id;
    try {
      await validateGatewayIpAddress(authenticatedInstance, {
        id,
        ipAddress,
      });

      return true;
    } catch {
      return t('details.information.duplicateIpAddressHelperText');
    }
  };

  const validateSingularityOfMediaServerGateway = async (gatewayType: GatewayType): Promise<boolean | string> => {
    if (gatewayType !== GatewayType.IoTEdgeMediaServerGatewayCluster) return true;

    try {
      await validateSingleMediaServerGateway(authenticatedInstance, {});
      return true;
    } catch {
      return t('details.information.mediaServerGatewayAlreadyExistsHelperText');
    }
  };

  const validateIpAddress = (ipAddress: string | undefined) => {
    ipAddress = ipAddress?.trim();
    if (!ipAddress) return true;

    const regex = new RegExp(RegexExpressions.IP_ADDRESS);
    if (!regex.test(ipAddress)) {
      return t('details.information.invalidIpAddressHelperText');
    }

    return true;
  };

  const validatePassword = (password: string) => {
    if (!password) return t('details.information.requiredHelperText');

    const regex = new RegExp(RegexExpressions.PASSWORD);
    if (!regex.test(password)) {
      return t('details.settings.offlinePortalCredentials.invalidPasswordHelperText');
    }

    return true;
  };

  const validateChildGatewayName = async (index: number): Promise<boolean | string> => {
    const childGateways = gatewayDetailsHook.form.getValues('childGateways');
    const childGateway = childGateways?.[index];
    const name = childGateway?.name?.trim();

    if (!name) return true;

    try {
      await validateGatewayName(authenticatedInstance, {
        id: childGateway?.id,
        name,
      });
    } catch {
      return t('details.information.uniqueNameHelperText');
    }

    const parentName = gatewayDetailsHook.form.getValues('name')?.trim();
    if (parentName && name.toLowerCase() === parentName.toLowerCase()) {
      return t('details.information.uniqueNameHelperText');
    }

    return true;
  };

  return {
    validateName,
    validateIpAddress,
    validateVirtualIpAddress,
    validatePassword,
    validateSingularityOfMediaServerGateway,
    validateChildGatewayName,
  };
};

export default useGatewayValidation;
